/**
 * LanguageAccessibilityPage — language + a11y settings.
 *
 * Persistence is dual:
 *   1. localStorage under `upmp.customer.preferences` — keeps unauthenticated
 *      pre-login flows working (the landing page, public tracking, etc. all
 *      respect the same prefs without a JWT).
 *   2. service-customer `PUT /v1/customers/:id` with the merged
 *      `preferences.accessibility` blob — so settings follow the citizen
 *      across devices once they sign in.
 *
 * On hydrate the server-side blob takes precedence over localStorage; if the
 * user lands here unauthenticated (stale token, fresh visit) we fall back to
 * the local copy.
 */
import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Ear, Eye, Globe, Loader2, Save, Type, Zap } from 'lucide-react';

import { ApiError } from '../../lib/api-client';
import { updateCustomer, type CustomerResponse } from '../../lib/api/customer';
import {
  DEFAULT_PREFERENCES,
  readClientPreferences,
  writeClientPreferences,
  type ClientPreferences,
  type LanguageCode,
  type TextSize,
} from '../../lib/preferences/clientPreferences';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { customerProfileQueryKey, useCustomerProfile } from './useCustomerProfile';
import { PageHeader } from './PortalUI';

const LANGUAGES: readonly { readonly value: LanguageCode; readonly label: string; readonly native: string }[] = [
  { value: 'en',  label: 'English',         native: 'English' },
  { value: 'ha',  label: 'Hausa',           native: 'Hausa' },
  { value: 'yo',  label: 'Yoruba',          native: 'Èdè Yorùbá' },
  { value: 'ig',  label: 'Igbo',            native: 'Asụsụ Igbo' },
  { value: 'pcm', label: 'Pidgin English',  native: 'Pidgin English' },
];

const TEXT_SIZES: readonly { readonly value: TextSize; readonly label: string; readonly cls: string }[] = [
  { value: 'small',   label: 'Small',   cls: 'text-xs' },
  { value: 'default', label: 'Default', cls: 'text-sm' },
  { value: 'large',   label: 'Large',   cls: 'text-base' },
];

const PREVIEW_TEXT =
  'Welcome to the Nigerian Postal Service. We are committed to delivering your packages safely and on time across Nigeria.';

interface ToggleProps {
  readonly enabled: boolean;
  readonly onToggle: () => void;
  readonly label: string;
}

function Toggle({ enabled, onToggle, label }: ToggleProps): JSX.Element {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={enabled}
      aria-label={label}
      className={`relative w-11 h-6 rounded-full transition-colors ${enabled ? 'bg-nipost-green' : 'bg-gray-300'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : 'translate-x-0'}`}
      />
    </button>
  );
}

function isLanguageCode(value: unknown): value is LanguageCode {
  return value === 'en' || value === 'ha' || value === 'yo' || value === 'ig' || value === 'pcm';
}
function isTextSize(value: unknown): value is TextSize {
  return value === 'small' || value === 'default' || value === 'large';
}

/** Read accessibility prefs out of the server-side `preferences.accessibility` blob. */
function readServerAccessibility(blob: Record<string, unknown> | undefined): ClientPreferences | null {
  if (!blob || typeof blob !== 'object') return null;
  const acc = (blob['accessibility'] ?? null) as unknown;
  if (!acc || typeof acc !== 'object') return null;
  const obj = acc as Record<string, unknown>;
  return {
    language: isLanguageCode(obj['language']) ? obj['language'] : DEFAULT_PREFERENCES.language,
    textSize: isTextSize(obj['textSize']) ? obj['textSize'] : DEFAULT_PREFERENCES.textSize,
    highContrast: typeof obj['highContrast'] === 'boolean' ? obj['highContrast'] : DEFAULT_PREFERENCES.highContrast,
    screenReader: typeof obj['screenReader'] === 'boolean' ? obj['screenReader'] : DEFAULT_PREFERENCES.screenReader,
    reducedMotion: typeof obj['reducedMotion'] === 'boolean' ? obj['reducedMotion'] : DEFAULT_PREFERENCES.reducedMotion,
  };
}

export function LanguageAccessibilityPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  const profile = useCustomerProfile();
  const [prefs, setPrefs] = useState<ClientPreferences>(DEFAULT_PREFERENCES);
  const [savedFlash, setSavedFlash] = useState<boolean>(false);
  const [hydrated, setHydrated] = useState<boolean>(false);

  // Hydrate from localStorage immediately so the form is usable pre-fetch.
  useEffect(() => {
    if (!hydrated) {
      setPrefs(readClientPreferences());
    }
  }, [hydrated]);

  // When the customer profile loads, prefer server-side prefs if present.
  useEffect(() => {
    if (!profile.data || hydrated) return;
    const server = readServerAccessibility(profile.data.preferences);
    if (server) {
      setPrefs(server);
      writeClientPreferences(server);
    }
    setHydrated(true);
  }, [profile.data, hydrated]);

  const mutation = useMutation<CustomerResponse, Error, ClientPreferences>({
    mutationFn: (next) => {
      if (!claims?.customerId) {
        throw new Error('Missing customer id in JWT.');
      }
      const merged: Record<string, unknown> = {
        ...(profile.data?.preferences ?? {}),
        accessibility: next,
      };
      return updateCustomer(
        claims.customerId,
        { preferences: merged },
        { bearerToken: token ?? undefined },
      );
    },
    onSuccess: (data, vars) => {
      if (claims?.customerId) {
        queryClient.setQueryData(customerProfileQueryKey(claims.customerId), data);
      }
      writeClientPreferences(vars);
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2500);
    },
  });

  function handleSave(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    // Always write through to localStorage so unauthed flows pick up.
    writeClientPreferences(prefs);
    if (claims?.customerId && token) {
      mutation.mutate(prefs);
    } else {
      // Unauthenticated — localStorage-only persistence still flashes saved.
      setSavedFlash(true);
      window.setTimeout(() => setSavedFlash(false), 2500);
    }
  }

  const errorMessage = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof ApiError && (mutation.error.status === 401 || mutation.error.status === 403)) {
      return 'Your session has expired. Please sign in again.';
    }
    return 'We couldn’t save your settings to the server. They’re saved on this device — try again to sync.';
  })();

  const previewClass = TEXT_SIZES.find((s) => s.value === prefs.textSize)?.cls ?? 'text-sm';

  return (
    <div>
      <PageHeader title="Settings" description="Language and accessibility preferences." />

      <form onSubmit={handleSave} className="max-w-2xl mx-auto space-y-6" noValidate>
        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-nipost-green" />
            <h2 className="font-semibold text-gray-900">Language</h2>
          </div>
          <p className="text-sm text-muted mb-4">Choose your preferred language for the portal.</p>
          <div className="space-y-2">
            {LANGUAGES.map((lang) => (
              <label
                key={lang.value}
                className={`flex items-center justify-between p-3.5 rounded-lg border cursor-pointer ${
                  prefs.language === lang.value ? 'border-nipost-green bg-nipost-green-light' : 'border-border hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="language"
                    value={lang.value}
                    checked={prefs.language === lang.value}
                    onChange={() => setPrefs((p) => ({ ...p, language: lang.value }))}
                    className="w-4 h-4 text-nipost-green focus:ring-nipost-green"
                  />
                  <span className="text-sm font-medium text-gray-900">{lang.label}</span>
                </div>
                <span className="text-sm text-muted">{lang.native}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Type className="w-5 h-5 text-nipost-green" />
            <h2 className="font-semibold text-gray-900">Text size</h2>
          </div>
          <div className="flex gap-2 mb-4">
            {TEXT_SIZES.map((s) => (
              <button
                key={s.value}
                type="button"
                onClick={() => setPrefs((p) => ({ ...p, textSize: s.value }))}
                className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium ${
                  prefs.textSize === s.value
                    ? 'border-nipost-green bg-nipost-green text-white'
                    : 'border-border bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className={`p-4 border border-border rounded-lg ${prefs.highContrast ? 'bg-gray-900 text-white border-gray-700' : 'bg-gray-50'}`}>
            <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-2">Preview</p>
            <p className={`${previewClass} ${prefs.highContrast ? 'text-white' : 'text-gray-700'} leading-relaxed`}>
              {PREVIEW_TEXT}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <Eye className="w-5 h-5 text-nipost-green mt-0.5" />
              <div>
                <h2 className="font-semibold text-gray-900">High contrast</h2>
                <p className="text-sm text-muted mt-0.5">Increase contrast for better visibility.</p>
              </div>
            </div>
            <Toggle
              enabled={prefs.highContrast}
              onToggle={() => setPrefs((p) => ({ ...p, highContrast: !p.highContrast }))}
              label="Toggle high contrast"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <Ear className="w-5 h-5 text-nipost-green mt-0.5" />
              <div>
                <h2 className="font-semibold text-gray-900">Screen reader optimisation</h2>
                <p className="text-sm text-muted mt-0.5">Enhance interface labels for screen readers.</p>
              </div>
            </div>
            <Toggle
              enabled={prefs.screenReader}
              onToggle={() => setPrefs((p) => ({ ...p, screenReader: !p.screenReader }))}
              label="Toggle screen reader optimisation"
            />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-nipost-green mt-0.5" />
              <div>
                <h2 className="font-semibold text-gray-900">Reduced motion</h2>
                <p className="text-sm text-muted mt-0.5">Disable animations and transitions.</p>
              </div>
            </div>
            <Toggle
              enabled={prefs.reducedMotion}
              onToggle={() => setPrefs((p) => ({ ...p, reducedMotion: !p.reducedMotion }))}
              label="Toggle reduced motion"
            />
          </div>
        </div>

        {savedFlash ? (
          <div className="rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2" role="status">
            <CheckCircle2 className="w-3.5 h-3.5" /> Saved &#x2713;
          </div>
        ) : null}

        {errorMessage ? (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800 flex items-center justify-between gap-2" role="alert">
            <span>{errorMessage}</span>
            <button
              type="button"
              onClick={() => mutation.mutate(prefs)}
              className="px-2 py-1 bg-white border border-red-200 rounded text-red-800 font-semibold hover:bg-red-50"
            >
              Try again
            </button>
          </div>
        ) : null}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="flex items-center gap-2 px-6 py-3 bg-nipost-green text-white font-semibold rounded-xl hover:bg-nipost-green-dark disabled:opacity-60"
          >
            {mutation.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Save settings
          </button>
        </div>

        <p className="text-xs text-muted text-center pb-4">
          Settings sync to your NIPOST account when you&apos;re signed in, and are also stored on this device under
          <span className="font-mono"> upmp.customer.preferences</span> so they apply pre-login.
        </p>
      </form>
    </div>
  );
}
